import { createSelector } from '@reduxjs/toolkit';
import { useAppLayoutId } from '@src/hooks/useAppLayoutId';
import type { AppNotification } from '@src/providers/AppNotifications';
import { getNotificationRenderInfo } from '@src/providers/AppNotifications/getNotificationRenderInfo';
import { notificationTypeToDisplayInfo } from '@src/providers/AppNotifications/notificationTypes/notificationTypeToDisplayInfo';
import type { UIAppNotificationState } from '@src/providers/AppNotifications/redux/AppNotificationsSlice';
import {
  archiveNotification,
  progressNotificationState,
} from '@src/providers/AppNotifications/redux/AppNotificationsSliceActions';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { navigationSliceActions } from '@src/providers/AppStateProvider/navigationSlice';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { compact } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppInboxToastBaseProps } from './AppInboxToastBase';
import { AppInboxToastWrapper } from './styles';

export interface AppInboxToastProps extends AppInboxToastBaseProps {
  notification: AppNotification;
  nextState: UIAppNotificationState;
}

const selectActiveToasts = createSelector(
  (state: AppState) => state.appNotifications.notificationMap,
  (state: AppState) => state.appNotifications.isReady,
  (notificationState, isReady) => {
    return isReady ? compact(Object.values(notificationState)) : undefined;
  }
);

export const AppInboxToastContainer = (toast: AppInboxToastBaseProps) => {
  const selectAllToasts = useAppStateSelector(selectActiveToasts);
  const activeToast = selectAllToasts?.find(notification => notification.notification.NotificationID === toast.id);
  // once found don't worry that it's going away;
  const [notification, setNotification] = useState(activeToast?.notification);
  useEffect(() => {
    // if the toast changes and is still active, update the notification.
    // don't worry about the case where the toast is removed, as it will be unmounted and we want to show the last state we had.
    if (activeToast) {
      setNotification(activeToast.notification);
    }
  }, [activeToast]);
  if (!notification) {
    return null;
  }
  return <AppInboxToast nextState="closed" {...toast} notification={notification} containerVariant="toast" />;
};

/** AppNotification Toasts, with UI driven  */
export function AppInboxToast({
  wrapperWidth, // Default width for the wrapper
  notification,
  nextState,
  containerVariant,
  ...toast
}: AppInboxToastProps & { containerVariant: 'inbox' | 'toast' }) {
  const dispatch = useAppStateDispatch();
  const selectedLayoutId = useAppLayoutId('trading');
  const onToastClick = useCallback(() => {
    const notificationRenderInfo = getNotificationRenderInfo(notification);
    const targetUrl = notificationRenderInfo.targetUrlResolver?.(selectedLayoutId) ?? notificationRenderInfo.targetUrl;
    if (targetUrl) {
      dispatch(navigationSliceActions.navigate(targetUrl));
    }
    if (containerVariant === 'inbox') {
      dispatch(
        archiveNotification({
          NotificationID: notification.NotificationID,
        })
      );
    }
  }, [dispatch, notification, containerVariant, selectedLayoutId]);

  const onRemove = useCallback(
    (id: string) => {
      if (nextState === 'closed') {
        dispatch(archiveNotification({ NotificationID: id }));
      } else {
        dispatch(
          progressNotificationState({
            NotificationID: id,
            state: nextState,
          })
        );
      }
    },
    [dispatch, nextState]
  );

  const ToastToRender = useMemo(
    () => notificationTypeToDisplayInfo[toast.appInboxToastType].toastContent,
    [toast.appInboxToastType]
  );

  return (
    <AppInboxToastWrapper
      addShadow={containerVariant !== 'inbox'}
      data-testid="toast-wrapper"
      data-severity={notification.Severity}
      w={wrapperWidth}
    >
      <ToastToRender
        {...toast}
        notification={notification}
        onRemove={onRemove}
        onToastClick={onToastClick}
        nextState={nextState}
        containerVariant={containerVariant}
      />
    </AppInboxToastWrapper>
  );
}
